/**
 * The card every Scores list sits in (Scores UX spec v1.0 section 2): the
 * `surface` card with a hairline border and the 12 radius, a header row
 * (title, optional live count, a trailing slot), then rows that bring their
 * own 12 inset and hairline separators. No gaps between rows.
 *
 * The header is at least 44 tall with 8 above and 4 below its content. Every
 * trailing control (See all, the Standings button) is a 36px visible box
 * whose 44px target is a transparent hit slop, so a one-line header is 48
 * tall on the All feed and in the league view alike.
 *
 * The title is never cut. A league's short name always fits one line, but the
 * league view's day heading ("Wednesday, Sept 30", "Week 12, Oct 30-Nov 3")
 * can be wider than what a 375 phone leaves beside a live count and the
 * Standings button, so the title wraps at spaces instead of clipping, and the
 * trailing slot stays centered on it.
 *
 * One frame for the league sections on the All feed, the Your teams card and
 * the league view, so the three can never drift apart.
 */
import type { ReactNode } from "react";

import { liveCountLabel } from "./leagueSelectorContract";

export type typeScoresSectionCardProps = {
  testId: string;
  /** Extra data attributes for the card root. */
  dataAttributes?: Record<`data-${string}`, string | undefined>;
  /** The header title: a league's short name, "Your teams", or the league
   *  view's human day heading. */
  title: string;
  titleTestId?: string;
  /** Games in play in this card. The count shows from 1. */
  liveCount?: number;
  /** The header's trailing slot: See all, or the Standings button. */
  trailing?: ReactNode;
  children: ReactNode;
};

export const ScoresSectionCard = (
  props: typeScoresSectionCardProps,
): ReactNode => {
  const liveCount = props.liveCount ?? 0;
  return (
    <section
      className="overflow-hidden rounded-md border border-hairline bg-surface"
      data-testid={props.testId}
      {...props.dataAttributes}
    >
      <div
        className="flex min-h-touch-target items-center gap-2 px-score-row-inset pt-2 pb-1"
        data-testid="scores-section-header"
      >
        {props.title === "" ? (
          <span
            className="h-4 w-1/3 rounded-sm bg-skeleton-base animate-skeleton motion-reduce:animate-none"
            aria-hidden="true"
            data-testid="scores-section-title-skeleton"
          />
        ) : (
          <h2
            className="min-w-0 break-words text-h2 text-ink"
            data-testid={props.titleTestId}
          >
            {props.title}
          </h2>
        )}
        {liveCount > 0 ? (
          <span
            className="flex shrink-0 items-center gap-score-live-gap text-small text-error"
            data-testid="scores-section-live-count"
          >
            <span
              className="h-score-live-dot w-score-live-dot rounded-full bg-error-solid"
              aria-hidden="true"
            />
            {liveCountLabel(liveCount)}
          </span>
        ) : null}
        {props.trailing !== undefined ? (
          <span
            className="ml-auto flex shrink-0 items-center"
            data-testid="scores-section-trailing"
          >
            {props.trailing}
          </span>
        ) : null}
      </div>
      {props.children}
    </section>
  );
};

/** How many of these events are in play, by the server's own flags. */
export function countInPlay(
  events: readonly { isLive: boolean; status: string }[],
): number {
  return events.filter((event) => event.isLive && event.status === "inProgress")
    .length;
}
